// src/pages/Sessions.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import DataTable from '../components/DataTable';
import Modal, { ModalConfirm } from '../components/Modal';
import { StatusBadge } from '../components/Badge';
import {
    ComputerDesktopIcon,
    DevicePhoneMobileIcon,
    GlobeAltIcon,
    TrashIcon,
    ArrowPathIcon,
    FunnelIcon,
    UserIcon,
    ClockIcon,
    ShieldCheckIcon,
    ExclamationTriangleIcon,
    EyeIcon
} from '@heroicons/react/24/outline';

const Sessions = () => {
    const { executeQuery, executeMutation, queries, mutations, loading, error } = useGraphQL();
    const [sessions, setSessions] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [filters, setFilters] = useState({
        userId: '',
        activeOnly: false
    });
    const [lastRefresh, setLastRefresh] = useState(null);

    useEffect(() => {
        loadSessions();
        loadUsers();
    }, [filters]);

    const loadUsers = async () => {
        try {
            const data = await executeQuery(queries.getUsers(), { limit: 500, offset: 0 });
            setUsers(data.adminUsers || []);
        } catch (err) {
            console.error('Failed to load users:', err);
        }
    };

    const loadSessions = async () => {
        try {
            const filterVars = {
                userId: filters.userId || undefined,
                activeOnly: filters.activeOnly || undefined,
                limit: 100,
                offset: 0
            };
            const data = await executeQuery(queries.getSessions(), filterVars);
            setSessions(data.adminSessions || []);
            setLastRefresh(new Date());
        } catch (err) {
            console.error('Failed to load sessions:', err);
        }
    };

    const handleDeleteSession = async () => {
        if (!selectedSession) return;
        try {
            const result = await executeMutation(mutations.adminDeleteSession, {
                id: selectedSession.id
            });

            if (result.adminDeleteSession?.success) {
                setShowDeleteModal(false);
                setSelectedSession(null);
                await loadSessions();
            } else {
                alert('Error: ' + (result.adminDeleteSession?.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Failed to delete session:', err);
            alert('Failed to delete session: ' + err.message);
        }
    };

    const isSessionActive = (session) => {
        if (!session.expiresAt) return false;
        return new Date(session.expiresAt) > new Date();
    };

    const parseUserAgent = (userAgent) => {
        if (!userAgent) return { browser: 'Unknown', os: 'Unknown', device: 'desktop' };

        let browser = 'Unknown';
        let os = 'Unknown';
        let device = 'desktop';

        // Detect browser
        if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
            browser = 'Chrome';
        } else if (userAgent.includes('Firefox')) {
            browser = 'Firefox';
        } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
            browser = 'Safari';
        } else if (userAgent.includes('Edg')) {
            browser = 'Edge';
        } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
            browser = 'Opera';
        }

        // Detect OS
        if (userAgent.includes('Windows')) {
            os = 'Windows';
        } else if (userAgent.includes('Mac OS')) {
            os = 'macOS';
        } else if (userAgent.includes('Linux')) {
            os = 'Linux';
        } else if (userAgent.includes('Android')) {
            os = 'Android';
            device = 'mobile';
        } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
            os = 'iOS';
            device = 'mobile';
        }

        return { browser, os, device };
    };

    const getUserById = (userId) => {
        return users.find(u => u.id === userId);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString();
    };

    const getTimeAgo = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    const columns = [
        {
            key: 'userId',
            title: 'User',
            render: (userId) => {
                const user = getUserById(userId);
                if (!user) {
                    return (
                        <div className="flex items-center space-x-2">
                            <div className="h-8 w-8 rounded-full bg-gray-600 flex items-center justify-center">
                                <UserIcon className="h-4 w-4 text-gray-400" />
                            </div>
                            <div>
                                <div className="text-gray-400 text-sm font-mono">{userId?.slice(0, 8)}...</div>
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="flex items-center space-x-2">
                        {user.avatar ? (
                            <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                            <div className="h-8 w-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-sm font-medium">
                                {(user.firstName?.[0] || '').toUpperCase()}{(user.lastName?.[0] || '').toUpperCase()}
                            </div>
                        )}
                        <div>
                            <div className="text-white font-medium">{user.email}</div>
                            <div className="text-gray-400 text-sm">{user.firstName} {user.lastName}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'userAgent',
            title: 'Device / Browser',
            render: (userAgent) => {
                const { browser, os, device } = parseUserAgent(userAgent);
                return (
                    <div className="flex items-center space-x-2">
                        {device === 'mobile' ? (
                            <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                            <ComputerDesktopIcon className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                            <div className="text-white">{browser}</div>
                            <div className="text-gray-400 text-sm">{os}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'ipAddress',
            title: 'IP Address',
            render: (ip) => (
                <div className="flex items-center space-x-2">
                    <GlobeAltIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-white font-mono text-sm">{ip || '-'}</span>
                </div>
            )
        },
        {
            key: 'createdAt',
            title: 'Created',
            sortable: true,
            render: (createdAt) => (
                <div>
                    <div className="text-white">{formatDate(createdAt)}</div>
                    <div className="text-gray-400 text-xs">{getTimeAgo(createdAt)}</div>
                </div>
            )
        },
        {
            key: 'expiresAt',
            title: 'Status',
            render: (expiresAt, session) => {
                const active = isSessionActive(session);
                return (
                    <div>
                        <div className="flex items-center space-x-2">
                            <StatusBadge status={active ? 'active' : 'expired'} />
                            {session.isImpersonation && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700">
                                    <EyeIcon className="h-3 w-3 mr-1" />
                                    Impersonation
                                </span>
                            )}
                        </div>
                        {active && (
                            <div className="text-gray-400 text-xs mt-1">
                                Expires: {formatDate(expiresAt)}
                            </div>
                        )}
                        {session.isImpersonation && session.impersonatedBy && (
                            <div className="text-amber-700 text-xs mt-1">
                                By: {session.impersonatedBy.email}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, session) => (
                <div className="flex space-x-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSession(session);
                            setShowDetailModal(true);
                        }}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="View details"
                    >
                        <ShieldCheckIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSession(session);
                            setShowDeleteModal(true);
                        }}
                        className="p-1 text-gray-400 hover:text-red-700"
                        title="Invalidate session"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    // Calculate stats
    const activeSessions = sessions.filter(isSessionActive).length;
    const expiredSessions = sessions.length - activeSessions;
    const uniqueUsers = new Set(sessions.map(s => s.userId)).size;
    const impersonationSessions = sessions.filter(s => s.isImpersonation).length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <ShieldCheckIcon className="h-8 w-8 text-primary" />
                    <div>
                        <h1 className="text-2xl font-bold text-white">Sessions</h1>
                        <p className="text-gray-400 text-sm">Monitor and manage user sessions</p>
                    </div>
                </div>
                <button
                    onClick={loadSessions}
                    disabled={loading}
                    className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 flex items-center space-x-2"
                >
                    <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-blue-500 bg-opacity-20 p-2 mr-3">
                            <ShieldCheckIcon className="h-5 w-5 text-blue-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Total Sessions</p>
                            <p className="text-xl font-semibold text-white">{sessions.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-green-500 bg-opacity-20 p-2 mr-3">
                            <ClockIcon className="h-5 w-5 text-green-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Active Sessions</p>
                            <p className="text-xl font-semibold text-green-700">{activeSessions}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-yellow-500 bg-opacity-20 p-2 mr-3">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Expired Sessions</p>
                            <p className="text-xl font-semibold text-yellow-700">{expiredSessions}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4">
                    <div className="flex items-center">
                        <div className="bg-purple-500 bg-opacity-20 p-2 mr-3">
                            <UserIcon className="h-5 w-5 text-purple-700" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-400">Unique Users</p>
                            <p className="text-xl font-semibold text-white">{uniqueUsers}</p>
                        </div>
                    </div>
                </div>
                {impersonationSessions > 0 && (
                    <div className="bg-gray-800 p-4 border border-amber-200">
                        <div className="flex items-center">
                            <div className="bg-amber-500 bg-opacity-20 p-2 mr-3">
                                <EyeIcon className="h-5 w-5 text-amber-700" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-400">Impersonation Sessions</p>
                                <p className="text-xl font-semibold text-amber-700">{impersonationSessions}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="bg-gray-800 p-4">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center space-x-2">
                        <FunnelIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-400 text-sm">Filters:</span>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <select
                            value={filters.userId}
                            onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Users</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.email} ({user.firstName} {user.lastName})
                                </option>
                            ))}
                        </select>
                    </div>
                    <label className="flex items-center space-x-2 text-sm text-gray-300">
                        <input
                            type="checkbox"
                            checked={filters.activeOnly}
                            onChange={(e) => setFilters(prev => ({ ...prev, activeOnly: e.target.checked }))}
                            className="bg-gray-700 border-gray-600 text-primary focus:ring-primary"
                        />
                        <span>Active Only</span>
                    </label>
                    <button
                        onClick={() => setFilters({ userId: '', activeOnly: false })}
                        className="px-3 py-1.5 bg-gray-700 text-white hover:bg-gray-600 text-sm"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Sessions Table */}
            <DataTable
                columns={columns}
                data={sessions}
                loading={loading}
                emptyMessage="No sessions found"
                onRowClick={(session) => {
                    setSelectedSession(session);
                    setShowDetailModal(true);
                }}
            />

            {/* Last Refresh */}
            {lastRefresh && (
                <div className="text-sm text-gray-500 text-center">
                    Last refreshed: {lastRefresh.toLocaleTimeString()}
                </div>
            )}

            {/* Session Detail Modal */}
            <Modal
                isOpen={showDetailModal}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedSession(null);
                }}
                title="Session Details"
                size="lg"
            >
                {selectedSession && (
                    <SessionDetailPanel
                        session={selectedSession}
                        user={getUserById(selectedSession.userId)}
                        parseUserAgent={parseUserAgent}
                        formatDate={formatDate}
                        isActive={isSessionActive(selectedSession)}
                    />
                )}
            </Modal>

            {/* Delete Confirmation Modal */}
            <ModalConfirm
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedSession(null);
                }}
                onConfirm={handleDeleteSession}
                title="Invalidate Session"
                message={`Are you sure you want to invalidate this session? The user will be logged out and will need to sign in again.`}
                confirmLabel="Invalidate"
                type="danger"
            />
        </div>
    );
};

// Session Detail Panel Component
const SessionDetailPanel = ({ session, user, parseUserAgent, formatDate, isActive }) => {
    const { browser, os, device } = parseUserAgent(session.userAgent);

    return (
        <div className="space-y-6">
            {/* User Info */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">User</h4>
                <div className="bg-gray-700 p-4">
                    {user ? (
                        <div className="flex items-center space-x-4">
                            {user.avatar ? (
                                <img src={user.avatar} alt="" className="h-12 w-12 rounded-full object-cover" />
                            ) : (
                                <div className="h-12 w-12 rounded-full bg-gray-600 flex items-center justify-center text-white text-lg font-medium">
                                    {(user.firstName?.[0] || '').toUpperCase()}{(user.lastName?.[0] || '').toUpperCase()}
                                </div>
                            )}
                            <div>
                                <div className="text-white font-medium">{user.email}</div>
                                <div className="text-gray-400">{user.firstName} {user.lastName}</div>
                                <div className="text-gray-500 text-sm">{user.role}</div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-gray-400">
                            <span className="font-mono">{session.userId}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Session Status */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Status</h4>
                <div className="bg-gray-700 p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <StatusBadge status={isActive ? 'active' : 'expired'} />
                            {session.isImpersonation && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700">
                                    <EyeIcon className="h-3 w-3 mr-1" />
                                    Impersonation
                                </span>
                            )}
                        </div>
                        {isActive ? (
                            <span className="text-green-700 text-sm">Session is valid</span>
                        ) : (
                            <span className="text-red-700 text-sm">Session has expired</span>
                        )}
                    </div>
                </div>
            </div>

            {/* Impersonation Info */}
            {session.isImpersonation && session.impersonatedBy && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Impersonation Details</h4>
                    <div className="bg-amber-50 bg-opacity-30 border border-amber-200 p-4">
                        <div className="flex items-center space-x-4">
                            <EyeIcon className="h-8 w-8 text-amber-700" />
                            <div>
                                <div className="text-amber-700 font-medium">
                                    Impersonated by Admin
                                </div>
                                <div className="text-amber-700">
                                    {session.impersonatedBy.email}
                                </div>
                                <div className="text-amber-700 text-sm">
                                    {session.impersonatedBy.firstName} {session.impersonatedBy.lastName}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Device Info */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Device Information</h4>
                <div className="bg-gray-700 p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <span className="text-gray-400 text-sm">Browser:</span>
                            <div className="text-white">{browser}</div>
                        </div>
                        <div>
                            <span className="text-gray-400 text-sm">Operating System:</span>
                            <div className="text-white">{os}</div>
                        </div>
                        <div>
                            <span className="text-gray-400 text-sm">Device Type:</span>
                            <div className="text-white capitalize">{device}</div>
                        </div>
                        <div>
                            <span className="text-gray-400 text-sm">IP Address:</span>
                            <div className="text-white font-mono">{session.ipAddress || '-'}</div>
                        </div>
                    </div>
                    <div>
                        <span className="text-gray-400 text-sm">User Agent:</span>
                        <div className="text-gray-300 text-xs mt-1 break-all bg-gray-800 p-2">
                            {session.userAgent || '-'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Timestamps */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Timestamps</h4>
                <div className="bg-gray-700 p-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <span className="text-gray-400 text-sm">Created:</span>
                            <div className="text-white">{formatDate(session.createdAt)}</div>
                        </div>
                        <div>
                            <span className="text-gray-400 text-sm">Expires:</span>
                            <div className={isActive ? 'text-white' : 'text-red-700'}>
                                {formatDate(session.expiresAt)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Session ID */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Session ID</h4>
                <div className="bg-gray-700 p-4">
                    <code className="text-gray-300 text-sm break-all">{session.id}</code>
                </div>
            </div>
        </div>
    );
};

export default Sessions;
